import type { Condition, DataType, OperatorId } from '@resource-kit/model';
import { OPERATOR_IDS } from '@resource-kit/model';
import { mergeConditionRows } from './domain';
import { wallTimeToUtcIso } from '../util/tz';

// Which calendar a bare day on an INSTANT column names (#2105).
//
//   - `'merchant'` — the operator's business day. The default, because that is
//     what an operator filtering "the 13th" means.
//   - `'utc'` — the day as the emitting system stamped it. Forensic columns
//     (audit rows, webhook receipts) keep it, because their whole value is
//     lining up against somebody else's log.
export type InstantReading = 'merchant' | 'utc';

// What a column is, for the purposes of completing a filter bound: a DAY
// column, a non-temporal type, or an INSTANT column together with the calendar
// its bare days are read in.
//
// `'datetime'` is deliberately absent. The two facts this map supplies —
// instant-vs-day, and which day reading — have the same producer
// (`deriveColumnTypes`) and the same consumer (this file), so they are one
// value rather than a value plus a flag that could contradict it. Removing the
// old member is also what makes the migration finite: every stale `'datetime'`
// literal is a type error rather than a silent fall-through to the default.
export type ColumnKind = Exclude<DataType, 'datetime'> | 'instant-merchant' | 'instant-utc';

// A field → column kind lookup, keyed by the id the condition carries
// (`filterId` when the field declares one). Built by `deriveColumnTypes`;
// handed in by `useDataTableParams` so this boundary can tell an INSTANT
// column from a DAY column — the one fact a filter value's own shape cannot
// supply (`2026-08-13` is a complete value for a `date` column and an
// incomplete one for an instant column).
export type ColumnTypeMap = Readonly<Record<string, ColumnKind>>;

// Which edge of a named unit a bound completes to. `'start'` is the first
// representable instant inside it, `'end'` the last one.
type BoundEdge = 'start' | 'end';

// The three sub-canonical shapes the pickers actually emit, each with the
// suffix that completes it at either edge of its own unit (#2094, #2102):
//   - `DateRangePicker` emits bare days for BOTH column types (it takes no
//     `withTime`), so a `between` on a datetime column arrives day-only;
//   - `DatePicker` with `withTime` emits `…THH:mm` — no seconds, no
//     milliseconds, no zone. No FILTER picker asks for it any more (#2111 — a
//     single-bound filter names a day), so nothing this serializer is handed
//     is freshly minted in that shape; it arrives only from URLs written
//     before #2111. Still handled, because those URLs are still bookmarked.
//     (The form picker keeps emitting `…THH:mm`, but form values travel the
//     mutation pipeline and never reach this serializer or the list kernel.)
// The backend's `isoInstantSchema` accepts EXACTLY `YYYY-MM-DDTHH:MM:SS.sssZ`,
// so every one of these is a 400 on an instant column.
//
// The `end` suffixes are safe as the LAST instant of the unit rather than as
// an exclusive next-unit start: the kernel pins stored instants to `.sssZ`
// precision, so no representable value sits between `…:59.999Z` and the next
// unit's zero.
//
// The suffixes below complete the value into a full WALL CLOCK reading —
// no zone. Which instant that clock reading names is the column's
// `InstantReading`: a `'utc'` column simply appends `Z`, a `'merchant'` column
// runs it through the merchant zone (#2105). This serializer is the only layer
// that could ever learn the operating timezone, which is the second reason the
// completion lives here and not in the kernel — it is handed the zone by name
// (`SerializeOptions.timeZone`) rather than reading it, so the layer stays
// pure while remaining the only one that knows.
//
// All three shapes are read on the SAME calendar. Splitting by precision —
// bare day as one reading, `THH:mm` as another — would make `2026-08-13` and
// `2026-08-13T00:00` name instants hours apart while reading as one moment.
//
// Anything else — an offset (`+08:00`), microseconds, garbage — passes through
// UNTOUCHED for the backend to reject with its actionable 400 (`expectedKind` /
// `expectedFormat` / `example` / `invalidBound` / `boundIndex`). Guessing here
// would turn a precise error into a silently different query.
const INSTANT_COMPLETIONS: readonly {
  readonly shape: RegExp;
  readonly start: string;
  readonly end: string;
}[] = [
  { shape: /^\d{4}-\d{2}-\d{2}$/, start: 'T00:00:00.000', end: 'T23:59:59.999' },
  { shape: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, start: ':00.000', end: ':59.999' },
  { shape: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/, start: '.000', end: '.999' },
];

// Which edge each operator's bounds complete to, indexed by bound POSITION —
// `between` carries two and wants one of each, every other operator carries a
// single kind and reuses its first entry (`in` repeats it across all members).
//
// The rule is uniform, not a special case bolted onto `to`: a bound completes
// to whichever edge makes the operator include or exclude the named unit
// WHOLLY. `from` (gte) and `lt` both need the unit's start — one to admit it,
// one to exclude it. `to` (lte) and `gt` both need its end, for the mirrored
// pair of reasons. That is what makes "到 X 日" contain day X, which is how a
// `date` column has always behaved (#2102).
//
// `eq` stays on the start edge: a bare day there matches almost nothing, but
// that predates this change and is tracked as #2106, not fixed in passing.
//
// Typed as a total `Record<OperatorId, …>`, so adding an operator to
// `OPERATOR_IDS` fails this declaration to compile rather than defaulting the
// new operator to some silent edge.
const BOUND_EDGES: Record<OperatorId, readonly [BoundEdge, ...BoundEdge[]]> = {
  eq: ['start'],
  ne: ['start'],
  lt: ['start'],
  gt: ['end'],
  like: ['start'],
  ilike: ['start'],
  in: ['start'],
  from: ['start'],
  to: ['end'],
  between: ['start', 'end'],
};

function boundEdge(op: OperatorId, index: number): BoundEdge {
  const edges = BOUND_EDGES[op];
  return edges[Math.min(index, edges.length - 1)];
}

/**
 * Complete one filter bound for an INSTANT column into the canonical
 * `YYYY-MM-DDTHH:MM:SS.sssZ` the list kernel accepts, padding to the requested
 * edge of whatever unit the value names and resolving that wall-clock reading
 * against `reading`'s calendar.
 *
 * Idempotent by construction under BOTH readings: an already-canonical value —
 * including the `…T23:59:59.999Z` this function itself emits — matches none of
 * the sub-canonical shapes and is returned unchanged, so re-serializing the URL
 * this function wrote is a no-op. That is what stops a merchant column from
 * shifting its own output by another offset on every round trip.
 *
 * `timeZone` is the operating zone as an IANA name, handed in rather than read
 * (#2167): this module is the only layer that could ever learn it, and reading
 * it from a host module here is what would have kept the whole query layer
 * nailed to fastcade. Under the `'utc'` reading it is not consulted at all.
 */
export function completeInstantBound(
  value: string,
  edge: BoundEdge,
  reading: InstantReading,
  timeZone: string,
): string {
  for (const completion of INSTANT_COMPLETIONS) {
    if (!completion.shape.test(value)) continue;
    const wall = `${value}${completion[edge]}`;
    return reading === 'utc' ? `${wall}Z` : wallTimeToUtcIso(wall, timeZone);
  }
  return value;
}

/** The reading an instant column declares, or `undefined` for a non-instant. */
function instantReadingOf(kind: ColumnKind | undefined): InstantReading | undefined {
  if (kind === 'instant-merchant') return 'merchant';
  if (kind === 'instant-utc') return 'utc';
  return undefined;
}

/**
 * Apply `completeInstantBound` to every bound of one condition's value, using
 * the edge `op` wants at that bound's position.
 *
 * `date` columns (and every non-temporal type, and every field the map does
 * not know) are returned IDENTICALLY: `YYYY-MM-DD` is the canonical shape for
 * a day column — and already inclusive at both ends under the kernel's
 * lexicographic comparison — so padding it there, at either edge, would
 * produce the very 400 this closes. A day column is also already a MERCHANT
 * calendar day by construction, so there is nothing for #2105 to convert.
 */
function completeConditionValue(
  value: unknown,
  kind: ColumnKind | undefined,
  op: OperatorId,
  timeZone: string,
): unknown {
  const reading = instantReadingOf(kind);
  if (!reading) return value;
  if (typeof value === 'string') {
    return completeInstantBound(value, boundEdge(op, 0), reading, timeZone);
  }
  if (Array.isArray(value)) {
    return value.map((bound, index) =>
      typeof bound === 'string'
        ? completeInstantBound(bound, boundEdge(op, index), reading, timeZone)
        : bound,
    );
  }
  return value;
}

// This is the UI ⇒ wire boundary, so it is where same-field rows are resolved.
//
// A URL search string cannot hold two values for one key, and the loop below
// writes `params[field] = …`, so before #1803 a second condition on a field
// SILENTLY overwrote the first: two chips on screen, one of them a lie.
// `mergeConditionRows` gives that collapse defined semantics (N×eq ⇒ `in`,
// from+to ⇒ `between`, otherwise last-write-wins) and hands the controller a
// notice for each one so the UI can say it out loud. With at most one condition
// per field — every case the 20 shipped resources produce — it is a no-op and
// the emitted params are byte-identical.
//
// `options.columnTypes` (#2094) is what lets this boundary complete a temporal bound
// into the shape the list kernel accepts. It is applied ONCE here, before the
// per-op switch, so EVERY op is covered, not just the ones the QueryBuilder
// can currently mint. `eq` is the case that proves why: #2106 took it out of
// the datetime operator registry, but a URL bookmarked before that still
// deserializes into `eq` on an instant column, and the backend validates it
// exactly like `from` / `to` / `between` — so patching only the range arms
// would leave those URLs 400-ing.
// `c.op` rides along because the completion is direction-aware (#2102): the
// operator, not the value, decides which edge of the named unit to pad to.
// The map's VALUE decides the other half (#2105): whose calendar day the
// padded wall clock names.
// Omitted ⇒ no completion at all (in-memory callers and the day-only dispatch
// presets), which is the pre-#2094 behavior byte for byte.
/**
 * The two halves arrive TOGETHER or not at all (#2179).
 *
 * The operating zone is an IANA name the caller passes: a default here would be
 * a zone this package invented, and a merchant completion resolved against the
 * wrong calendar is WELL-FORMED — `to:2026-08-13` still comes back as a
 * canonical instant, just the wrong one, which no caller and no assertion
 * downstream can tell from the right one. That is invariant 5 (#2167): the
 * zone is passed in, never global state and never guessed.
 *
 * `columnTypes` is what decides whether a zone is consulted at all, and the
 * union says so instead of a comment saying so. `completeConditionValue`
 * returns early — see `completeConditionValue` above — on a field the map does
 * not name as an instant
 * column, and with no map every field is such a field — so a bag carrying a
 * zone and no map carries a value nothing will read. Two call sites used to
 * explain that in prose; one of them took a Provider dependency to produce the
 * value it then explained away.
 *
 * The second member spells `timeZone?: undefined` to refuse a bag that is NOT a
 * fresh object literal. Measured 2026-09-07 on this repo's tsc 5.8.3, by
 * deleting the member and reading what changed: an inline `{ timeZone: 'UTC' }`
 * is refused either way (tsc reports it as missing the first member's required
 * `columnTypes`), so that shape cannot witness this member. What DOES change is
 * a hoisted `const bag = { columnTypes: undefined, timeZone: 'UTC' }`: refused
 * with the member, accepted without it. `serialize.test.ts` pins both shapes
 * with `@ts-expect-error`, and the hoisted one is the case that reds with
 * TS2578 when this member is deleted.
 */
export type SerializeOptions =
  | { readonly columnTypes: ColumnTypeMap; readonly timeZone: string }
  | { readonly columnTypes?: undefined; readonly timeZone?: undefined };

export function serializeConditionsToParams(
  conditions: Condition[],
  options: SerializeOptions,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const c of mergeConditionRows(conditions).conditions) {
    const key = c.field;
    // The union's discriminant, read as one: no map ⇒ no completion, which is
    // also why there is no zone to pass. `completeConditionValue`'s signature is
    // unchanged — the early return it used to take on every field is now simply
    // never reached from this arm.
    const val = options.columnTypes
      ? completeConditionValue(c.value, options.columnTypes[c.field], c.op, options.timeZone)
      : c.value;
    // Skip empty strings/arrays to avoid sending blank filters
    if (val === undefined || val === null) continue;
    if (typeof val === 'string' && val.trim() === '') continue;
    if (Array.isArray(val) && val.length === 0) continue;
    switch (c.op) {
      case 'eq':
        params[key] = String(val);
        break;
      case 'ne':
      case 'lt':
      case 'gt':
      case 'like':
      case 'ilike':
        params[key] = `${c.op}:${String(val)}`;
        break;
      case 'in': {
        const arr = Array.isArray(val)
          ? val
          : String(val)
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
        if (arr.length > 0) params[key] = `in:${arr.join(',')}`;
        break;
      }
      case 'from':
      case 'to':
        params[key] = `${c.op}:${String(val)}`;
        break;
      case 'between': {
        const [a, b] = val as [string, string];
        if (a && b) params[key] = `between:${a},${b}`;
        break;
      }
    }
  }
  return params;
}

// URL keys that have reserved meaning (pagination / sort / search) and
// are NOT condition fields. EQD §1: this is the ONE definition on the
// frontend side. The host used to re-export this binding from
// `src/components/query/serialize.ts`; #2170 deleted that shim, so every
// importer now names `@resource-kit/controllers` and no one restates the list.
//
// NO CROSS-PACKAGE GUARD PINS THIS TODAY. Nothing under `backend/src`
// names `RESERVED_QUERY_KEYS`, and the kernel's own reserved set
// (`backend/src/platform/http/list-query.ts::RESERVED_KEYS`) is a DIFFERENT, smaller
// set — `page`, `size`, `sort`, `search`. This list additionally reserves
// `limit` and `order`, which that file's header records the #932
// adjudication as having dropped, calling them legacy aliases
// (in that file's header comment). So the two lists are not equal, there is no
// equality for a test to assert, and no test asserts any weaker
// relationship either. Adding a key here is therefore unguarded against
// the backend: check `list-query.ts` by hand. (An equality test did once
// exist for the ARCHIVED backend —
// `archive-snapshot-2026-10:archive/backend/src/__tests__/utils/list-filter.test.ts`, its
// `it('RESERVED_QUERY_KEYS matches backend RESERVED_KEYS literal-by-literal')` —
// but
// `archive/` is read-only history and that file is not run.)
export const RESERVED_QUERY_KEYS = ['page', 'size', 'limit', 'order', 'sort', 'search'] as const;
const RESERVED_KEYS: Set<string> = new Set(RESERVED_QUERY_KEYS);

// Page-internal URL keys (the `_`-prefixed family skipped by the decoder
// below). These are NOT condition fields and never reach the data API;
// they carry presentation / intent state across back/refresh navigation.
// EQD §1: this cross-module string contract (written by ResourcePage's
// preset toggle + useDataTableParams' clear-all sentinel, read back by
// both) is defined ONCE here so it can't drift between producers/readers.
//   - `preset`:  which FilterPreset button is highlighted (ResourcePage)
//   - `cleared`: operator explicitly cleared all filters, so an empty URL
//                must NOT re-apply defaultConditions (useDataTableParams)
//   - `tab`:     which VIEW of a two-view list is showing (#2004). Same class
//                as `preset` — presentation intent that must survive a refresh
//                or a pasted link — and it needs the same `_` prefix for the
//                same reason: a bare `tab=` reads as a condition field, and
//                the list dropped it with a whitelist warning on every load.
export const INTERNAL_QUERY_KEYS = {
  preset: '_preset',
  cleared: '_cleared',
  tab: '_tab',
} as const;

// Single source of truth for "is this URL key a real condition field?" —
// i.e. NOT a reserved pagination/sort/search key and NOT a `_`-prefixed
// page-internal key (`_preset`/`_cleared`). The decoder here, plus the
// clear-all loop and the `_cleared` invariant check in useDataTableParams,
// all classify keys the same way; defining it once stops that judgement
// from drifting across the three call sites (EQD §1).
export function isConditionKey(key: string): boolean {
  return !RESERVED_KEYS.has(key) && !key.startsWith('_');
}

// Operator-prefix regex DERIVED from `OPERATOR_IDS`, so this file holds
// no second spelling of the op list to drift against the first: adding an
// op to `OPERATOR_IDS` in `@resource-kit/model` updates this regex on its
// own.
//
// HOW THAT REACHES THE BACKEND, AND HOW FAR IT REACHES. There is no
// backend counterpart to this regex — nothing under `backend/src` names
// `OP_PREFIX_RE`; the kernel matches the `<op>:` prefix off its own
// `FILTER_OPS` tuple in `platform/http/list-query.ts`. What IS pinned is
// the tuple: `backend/src/__tests__/node/operator-parity-1743.test.ts`
// reads both source files and asserts `OPERATOR_IDS` and `FILTER_OPS` are
// the same closed set. So parity between this regex and the backend
// parser is TRANSITIVE — regex ⇐ `OPERATOR_IDS` ⇔ `FILTER_OPS` ⇒ backend
// parse — and never asserted directly. The link this file contributes to
// that chain is the derivation below; keep it derived, because a
// hand-spelled regex here would break the chain silently while every
// existing test stayed green.
const OP_PREFIX_RE = new RegExp(`^(?:${OPERATOR_IDS.join('|')}):`, 'i');

const KNOWN_OPS = new Set<OperatorId>(OPERATOR_IDS);

// Inverse of `serializeConditionsToParams`: decode a URL search-params
// shape back into the in-memory `Condition[]` form the QueryBuilder
// (and `useDataTableParams`) consume. Used by issue #590 to hydrate
// list-table state from the URL on initial render so refresh / share /
// back navigation all restore the same view.
//
// Decoding choices mirror backend `parseListQueryFromUrl`:
//   - reserved pagination/sort/search keys are skipped
//   - prefixed values (`ne:`, `like:`, `between:` …) decode to their op
//   - bare values become `eq`
//   - bare values containing `,` become `in` (matches backend)
//   - URI-shaped values (`https://x`, `mailto:y`) do NOT match
//     OP_PREFIX_RE — they decode to `eq` with the raw value
//   - malformed `between:` (not exactly two bounds) is dropped silently
//     — matches the backend's tolerance at the operator-typed URL
//     boundary (EQD §2). This is an allow-listed silent drop, not a
//     general catch-all swallow.
//   - "OP_PREFIX_RE matched but op is not in KNOWN_OPS" can only fire
//     if the regex derivation drifts from OPERATOR_IDS — we fail loud
//     (throw) rather than coerce to eq, per EQD §3.
//
// Stable `id` assignment: `${field}::${op}` so React keys are stable
// across re-renders for the same URL — preventing input focus loss
// and unnecessary remounts.
export function deserializeParamsToConditions(sp: URLSearchParams): Condition[] {
  const conditions: Condition[] = [];
  // Dedupe by key so query-string duplicates (e.g. `?status=a&status=b`)
  // only contribute one condition row — first value wins to match the
  // backend behavior described in `parseListQueryFromUrl`.
  const seen = new Set<string>();
  for (const key of sp.keys()) {
    // Skip reserved pagination/sort/search keys and `_`-prefixed
    // page-internal state (`_preset`/`_cleared`): neither is a condition
    // field, so neither is sent to the data API. See `isConditionKey`.
    if (!isConditionKey(key)) continue;
    if (seen.has(key)) continue;
    seen.add(key);

    const raw = sp.get(key) ?? '';
    if (!raw) continue;

    let op: OperatorId = 'eq';
    let value: Condition['value'] = raw;

    if (OP_PREFIX_RE.test(raw)) {
      // OP_PREFIX_RE is derived from OPERATOR_IDS, so anything matching
      // is by construction a KNOWN_OPS member — no "unknown prefix"
      // branch is reachable here. The `KNOWN_OPS.has` check kept below
      // as a TypeScript narrowing aid + tripwire if the regex
      // construction ever drifts from OPERATOR_IDS in a refactor.
      const idx = raw.indexOf(':');
      const opStr = raw.slice(0, idx).toLowerCase() as OperatorId;
      const rest = raw.slice(idx + 1);
      if (!KNOWN_OPS.has(opStr)) {
        // Defensive: regex derivation drifted from KNOWN_OPS. Fail
        // loud rather than silently coercing to eq — EQD §3.
        throw new Error(
          `deserializeParamsToConditions: OP_PREFIX_RE matched '${opStr}:' but op is not in KNOWN_OPS — OPERATOR_IDS / OP_PREFIX_RE drift`,
        );
      }
      op = opStr;
      if (op === 'in') {
        const arr = rest
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (arr.length === 0) continue;
        value = arr;
      } else if (op === 'between') {
        const parts = rest
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (parts.length !== 2) continue;
        value = [parts[0], parts[1]] as [string, string];
      } else {
        value = rest;
      }
    } else if (raw.includes(',')) {
      const arr = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (arr.length === 0) continue;
      op = 'in';
      value = arr;
    }

    conditions.push({ id: `${key}::${op}`, field: key, op, value });
  }
  return conditions;
}

export function toOrderParam(sort: string): string {
  const [field, dirMaybe] = (sort || '').split(':');
  const dir = dirMaybe?.toLowerCase() === 'asc' ? 'asc' : 'desc';
  return `${field || 'created_at'}:${dir}`;
}
